import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Params } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BurdSameDayDeliveryService } from '@shared/same-day-delivery/burd-same-day-delivery.service';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { NotificationService } from '@core/services/notification/notification.service';
import { Observable, of } from 'rxjs';
import { BurdDeliveryLeftTime } from '@models/api-orders.models';
import { BurdResponse } from '@features/checkout/checkout-delivery-burd/checkout-delivery-burd.models';
import { TranslationKey } from '@shared/translations/auto-generated/translation-key';
import { NotificationType } from '@core/services/notification/notification';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { SameDayDeliveryParams } from '@features/product-filter/same-day-delivery-facet/same-day-delivery-facet.model';
import { SameDayDeliveryStatus } from '@shared/same-day-delivery/same-day-delivery.model';
import { NUM_OF_CHARS_FOR_SEARCH_ZIP_CODE } from '@constants/same-day-delivery.constants';
import { secondsLeftToDate } from '@shared/same-day-delivery/same-day-delivery.helper';
import { GtmService } from '@core/services/gtm/gtm.service';
import { TimeLimitPipe } from '@shared/same-day-delivery/time-limit.pipe';
import { SafePipe } from '@shared/safe/safe.pipe';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { AsyncPipe, DatePipe, NgClass, NgIf } from '@angular/common';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';

@UntilDestroy()
@Component({
    selector: 'app-same-day-delivery-facet',
    templateUrl: './same-day-delivery-facet.component.html',
    styleUrls: ['./same-day-delivery-facet.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        LoadingDotsComponent,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        SvgIconComponent,
        TooltipComponent,
        NgClass,
        AsyncPipe,
        DatePipe,
        SafePipe,
        TranslateSafePipe,
        TimeLimitPipe
    ]
})
export class SameDayDeliveryFacetComponent implements OnInit {
    private sameDayDeliveryParams: SameDayDeliveryParams = {
        sameDayDeliveryIsActive: false,
        onTimeForSameDayDelivery: false,
        deliverToZipCode: false
    };

    public sameDayDeliveryStatus: SameDayDeliveryStatus = 'Idle';
    public timeLeft$: Observable<BurdDeliveryLeftTime>;
    public checkboxControl: UntypedFormControl = new UntypedFormControl(false);
    public zipCodeControl: UntypedFormControl = new UntypedFormControl('');

    @Input() public isActive: boolean = false;
    @Output() public sameDayDeliveryChange: EventEmitter<SameDayDeliveryParams> = new EventEmitter();

    constructor(
        private readonly translateSafe: TranslateSafePipe,
        private readonly sameDayDeliveryService: BurdSameDayDeliveryService,
        private readonly notificationService: NotificationService,
        private readonly gtmService: GtmService
    ) {
        this.timeLeft$ = this.sameDayDeliveryService.getBurdDeliveryTimeLeft().pipe(untilDestroyed(this));
    }

    ngOnInit(): void {
        // check if we should trigger the notification the zip code is already filled out
        // in case we have the zip code should the trigger notification should be false.
        let triggerNotification: boolean = !this.sameDayDeliveryService.getSameDayDeliveryZipCode();

        this.zipCodeControl.valueChanges
            .pipe(
                debounceTime(400),
                tap(zipCode => {
                    if (zipCode.length < NUM_OF_CHARS_FOR_SEARCH_ZIP_CODE) {
                        this.sameDayDeliveryStatus = 'Idle';
                        this.sameDayDeliveryService.setSameDayDeliveryZipCode('');
                        this.checkboxControl.patchValue(false);
                        this.checkboxControl.disable();
                        return;
                    }

                    this.checkboxControl.enable();
                    this.sameDayDeliveryService.setSameDayDeliveryZipCode(zipCode);
                }),
                filter(zipCode => zipCode.length >= NUM_OF_CHARS_FOR_SEARCH_ZIP_CODE),
                tap(zipCode => this.gtmService.trackSameDayDelivery(zipCode, 'plp')),
                switchMap(zipCode => this.sameDayDeliveryService.getBurdZipApproval(zipCode)),
                switchMap(burdZipApproval => this.checkSameDayDelivery(burdZipApproval)),
                untilDestroyed(this)
            )
            .subscribe(({ deliverToZipCode = false, secondsLeft, onTimeForSameDayDelivery = false }) => {
                const sameDayDeliveryIsActive = deliverToZipCode || false;
                this.checkboxControl.patchValue(sameDayDeliveryIsActive);
                this.sameDayDeliveryParams = { sameDayDeliveryIsActive, onTimeForSameDayDelivery, deliverToZipCode };

                this.sameDayDeliveryStatus = sameDayDeliveryIsActive ? 'Valid' : 'Invalid';

                this.sameDayDeliveryChange.emit(this.sameDayDeliveryParams);

                if (!triggerNotification) {
                    triggerNotification = true;
                    return;
                }

                this.handleNotifications(
                    deliverToZipCode,
                    onTimeForSameDayDelivery,
                    this.zipCodeControl.value,
                    secondsLeft
                );
            });

        this.checkboxControl.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
            this.sameDayDeliveryParams.sameDayDeliveryIsActive = value;
            this.sameDayDeliveryService.setSameDayDelivery(value);
            this.sameDayDeliveryChange.emit(this.sameDayDeliveryParams);
        });

        const savedZipCode = this.sameDayDeliveryService.getSameDayDeliveryZipCode();
        this.zipCodeControl.setValue(savedZipCode);
    }

    public onClearField = (): void => {
        this.zipCodeControl.setValue('');
        this.checkboxControl.setValue(false);
    };

    private checkSameDayDelivery(
        burdZipApproval: BurdResponse | undefined
    ): Observable<Partial<BurdResponse & BurdDeliveryLeftTime>> {
        if (burdZipApproval?.deliverToZipCode) {
            return this.timeLeft$.pipe(
                map(timeLeft => ({ ...burdZipApproval, ...timeLeft })),
                untilDestroyed(this)
            );
        }

        return of(burdZipApproval || {});
    }

    private handleNotifications(
        deliverToZipCode: boolean,
        onTimeForSameDayDelivery: boolean,
        zipCode: string,
        secondsLeft?: number
    ): void {
        if (!deliverToZipCode) {
            this.createNotification('info', 'sameDayDelivery.notifications.notAvailable', {
                zipCode: zipCode
            });
            return;
        }

        if (deliverToZipCode && !onTimeForSameDayDelivery) {
            this.createNotification('success', 'sameDayDelivery.notifications.forTomorrow', {
                zipCode: zipCode
            });

            return;
        }

        this.createNotification('success', 'sameDayDelivery.notifications.forToday', {
            zipCode: zipCode,
            deliveryCutoffTime: secondsLeft ? secondsLeftToDate(secondsLeft).getHours() : ''
        });
    }

    private createNotification(type: NotificationType, key: TranslationKey, params: Params): void {
        const message = this.translateSafe.transform(key, params);
        // we didn't use the notification translationText because we need to use params on it.
        this.notificationService.createNotification({
            type,
            showHeadline: false,
            messageText: message,
            fadeTime: 10000
        });
    }
}
